import {AnalysisGraphQL, GetAnalysisDataFrame, isAnalysisInputStepType} from 'analysis-tool/AnalysisTypes';
import {isTopLevelDataFrameView} from 'analysis-tool/data-view/AnalysisViewUtils';
import {INITIAL_DOCUMENT_LAYOUT_CONFIGURATION} from 'analysis-tool/outputs/ConfigureDocumentUtil';
import {AnalysisOutput, OutputDocumentLayoutConfiguration} from 'analysis-tool/outputs/Types';
import {assertAndContinue} from 'Asserts';
import {DataFrameColumnDef} from 'data-frames/DataFrameTypes';
import {PipelineStepDataKind} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  PipelineGraphQL,
  PipelineStep,
  PipelineViewGraphQL,
  PipelineViewLineageNode,
} from 'pipelines/PipelineTypes';
import {getOutputDataForLineageNode, resolveLineageStep} from 'pipelines/PipelineViewUtils';
import {omit} from 'util/omitKey';

import {CreateAnalysisTemplateDataFrameVariableParams} from './AnalysisTemplateDAO';

/**
 * @package
 */
export enum InputTableType {
  STATIC = 'STATIC',
  VARIABLE = 'VARIABLE',
}

interface DataFrameVariable {
  id: string;
  name: string;
  description: string;
  replaceStepId: string;
  replaceStepDataId: string;
  replaceStepDataIndex: number;
  replaceDataFrameColumnsSpec: Array<DataFrameColumnDef>;
}

/**
 * An input table. Used when configuring an analysis template.
 * @package
 */
export interface InputTableConfigState {
  inputType: InputTableType;
  variable: DataFrameVariable | null;
  lineageNode: PipelineViewLineageNode;
  description: string;
}

/** @package */
export interface AnalysisTemplateConfigState {
  inputTables: Array<InputTableConfigState>;
  name: string;
  templateCollectionId: string | null;
  selectedOutputs: Array<AnalysisOutput>;
  outputDocumentLayoutConfiguration: OutputDocumentLayoutConfiguration;
  lockResultTables?: boolean;
}

/** @package */
export function getDefaultTemplateConfigStateForAnalysis(
  analysis: AnalysisGraphQL
): AnalysisTemplateConfigState {
  return {
    name: '',
    inputTables: getRootTablesFromPipeline(analysis, [], InputTableType.VARIABLE),
    templateCollectionId: null,
    selectedOutputs: [],
    outputDocumentLayoutConfiguration: INITIAL_DOCUMENT_LAYOUT_CONFIGURATION,
  };
}

/** @package */
export function getTemplateDataForLineageNode(
  lineageNode: PipelineViewLineageNode,
  steps: ReadonlyArray<PipelineStep>
): Omit<CreateAnalysisTemplateDataFrameVariableParams, 'description'> {
  const dataFrame = getOutputDataForLineageNode(lineageNode, steps, PipelineStepDataKind.DATASET);
  return {
    name: dataFrame.name,
    replaceStepId: lineageNode.step.id,
    replaceStepDataId: lineageNode.stepOutputReference!.outputStepData.id,
    replaceStepDataIndex: lineageNode.stepOutputReference!.rawDataIndex!,
    replaceDataFrameColumnsSpec: dataFrame.columns,
  };
}

/**
 * Get the root table views of an analysis, which correspond to the VARIABLE and STATIC input tables of an analysis
 * template.
 *
 * @param pipeline The pipeline to turn into an analysis template OR the pipeline backing an analysis template
 * @param dataFrameVariables If the pipeline is backing an analysis template, the template's data frame
 *                           variables. Each `DataFrameVariable` corresponds to a root table in `pipeline`.
 * @param defaultInputType If the pipeline is to be turned into a pipeline, whether input tables should
 *                         default to VARIABLE or STATIC.
 *
 * @package exported for testing
 */
export function getRootTablesFromPipeline(
  pipeline: PipelineGraphQL,
  dataFrameVariables: Array<DataFrameVariable>,
  defaultInputType: InputTableType = InputTableType.STATIC
): Array<InputTableConfigState> {
  return pipeline.views
    .filter(
      (view) =>
        isTopLevelDataFrameView(view, pipeline) &&
        isAnalysisInputStepType(resolveLineageStep(pipeline.steps, view.lineage[0]).stepType)
    )
    .map((view) => {
      const templateData = getTemplateDataForLineageNode(view.lineage[0], pipeline.steps);
      const dataFrameVariable = dataFrameVariables.find(
        (dfv) =>
          dfv.replaceStepDataId === templateData.replaceStepDataId &&
          dfv.replaceStepDataIndex === templateData.replaceStepDataIndex &&
          dfv.replaceStepId === templateData.replaceStepId
      );
      return {
        inputType: dataFrameVariable ? InputTableType.VARIABLE : defaultInputType,
        variable: dataFrameVariable ?? null,
        lineageNode: view.lineage[0],
        description: dataFrameVariable ? dataFrameVariable.description : '',
      };
    });
}

/**
 * An input table. Use when applying an analysis template within the analysis tool.
 *
 * See `AnalysisTemplateVariableInputTable` for a narrower interface for external consumptions, which only
 * exposes persisted variable tables.
 */
export interface InputTableApplyState {
  inputType: InputTableType;
  dataFrameColumns: Array<DataFrameColumnDef> | null;
  templateLineageNode: PipelineViewLineageNode | null;
  variable: DataFrameVariable | null;
  table: PipelineViewGraphQL | null;
  description: string;
  columnMapping: {[templateColumn: string]: string};
}

/**
 * Get the `InputTableApplyState`s for an analysis template to apply it to an analysis.
 *
 * Input table apply states are a super set of `getRootTablesFromPipeline`. All root tables are included and we
 * give them a different input type based on wether there's a corresponding data frame variable to that root table
 * or not. Additionally, we add extra input tables for data frame variables that don't have a corresponding root table,
 * which happens when a template exists without raw datas and with data frame variables' replaceDataFrameColumnsSpec defined..
 *
 * When a root table does exist for an input table apply state, `templateLineageNode` is defined to point to that table, and
 * we can use that to build column mapping and preview UIs. Otherwise, only `dataFrameColumns` is defined, which still allows
 * use to build column mapping UIs but not preview UIs.
 *
 * @param pipeline The pipeline to turn into an analysis template OR the pipeline backing an analysis template
 * @param dataFrameVariables If the pipeline is backing an analysis template, the template's data frame
 *                           variables. Each `DataFrameVariable` corresponds to a root table in `pipeline`.
 * @param defaultInputType If the pipeline is to be turned into a pipeline, whether input tables should
 *                         default to VARIABLE or STATIC.
 */
export function getInputTableApplyStates(
  pipeline: PipelineGraphQL,
  dataFrameVariables: Array<DataFrameVariable>,
  defaultInputType: InputTableType = InputTableType.STATIC
): Array<InputTableApplyState> {
  const rootTables = getRootTablesFromPipeline(pipeline, dataFrameVariables, defaultInputType);

  const rootTableApplyStates: Array<InputTableApplyState> = rootTables.map((inputTable) => ({
    ...omit(inputTable, 'lineageNode'),
    templateLineageNode: inputTable.lineageNode,
    dataFrameColumns: getOutputDataForLineageNode(
      inputTable.lineageNode,
      pipeline.steps,
      PipelineStepDataKind.DATASET
    ).columns,
    table: null,
    columnMapping: {},
  }));

  const dataFrameVariablesWithoutRootTable = dataFrameVariables.filter(
    (dataFrameVariable) =>
      !rootTables.some(
        ({variable: otherDataFrameVariable}) => dataFrameVariable.id === otherDataFrameVariable?.id
      )
  );

  const nonRootTableApplyStates: Array<InputTableApplyState> = dataFrameVariablesWithoutRootTable.map(
    (dataFrameVariable) => ({
      inputType: InputTableType.VARIABLE,
      variable: dataFrameVariable,
      templateLineageNode: null,
      dataFrameColumns: dataFrameVariable.replaceDataFrameColumnsSpec,
      table: null,
      columnMapping: {},
      description: dataFrameVariable.description,
    })
  );

  return [...nonRootTableApplyStates, ...rootTableApplyStates];
}

/**
 * A variable input table to an analysis template.
 */
export interface AnalysisTemplateVariableInputTable {
  /**
   * The input table variable.
   *
   * • `variable.description` is the user-facing description of the input table.
   * • `variable.id` identifies the input table.
   * • `variable.name` is the title of the analysis template input table. Currently, this is copied from and
   *                   always equal to `dataFrame.name`, but may be configurable in the future.
   */
  variable: Pick<DataFrameVariable, 'id' | 'name' | 'description'>;
  /**
   * The data frame backing the analysis template input table, which is copied from the prototype analysis.
   *
   * • `dataFrame.allColumnsJson` defines the expected columns:
   *     • `dataFrame.allColumnsJson[i].systemName` identifies the column in column mappings.
   *     • `dataFrame.allColumnsJson[i].type` is the expected column type. This is loosely enforced, as a
   *        template may still succeed with columns of different types.
   * • `dataFrame.id` is not used to identify the input table (see `variable.id`), but can be used to preview
   *                  the input table (including its row contents) from the prototype analysis.
   * • `dataFrame.name` is the title of the table in the prototype analysis.
   */
  dataFrame: GetAnalysisDataFrame;
}

/**
 * Given a InputTableApplyState, return its display name.
 *
 * @param inputTableApplyState To get the name from
 * @returns If `templateLineageNode` is defined, we return the name from the backing data frame. Otherwise, we return the name
 * stored on the variable.
 */
export function getInputTableApplyStateName(
  inputTableApplyState: InputTableApplyState,
  steps: ReadonlyArray<PipelineStep>
): string {
  const maybeName =
    inputTableApplyState.templateLineageNode != null
      ? getOutputDataForLineageNode(
          inputTableApplyState.templateLineageNode,
          steps,
          PipelineStepDataKind.DATASET
        ).name
      : inputTableApplyState.variable?.name;
  assertAndContinue(maybeName != null, 'Expected either variable or templateLineageNode to be non-null');
  return maybeName ?? 'Table';
}
